/**
 * The settings page's fields (ADR 0172), as Mappings → Email and Network
 * access have them: a number saves when its field is left or Enter is pressed
 * (Escape puts it back), a checkbox as it's changed. Under the field,
 * "Saving…" then "Saved."; a value that can't be saved is explained there as
 * "Not saved: …" and isn't sent, and a refused one goes back to what's saved,
 * with the server's reason.
 *
 * One save at a time for each section: a change made meanwhile is sent once
 * that's answered (useSettingsSection.ts).
 */
import { type ReactNode, useId, useState } from "react";

export const inputClass =
  "h-10 rounded-input border border-line bg-card px-3 text-text " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand";

export const legendClass = "px-1 text-[12.5px] font-medium uppercase tracking-[0.08em] text-muted";

export const linkClass = "font-medium text-brand-ink underline underline-offset-2";

export function Status({ status }: { status: string | undefined }) {
  return (
    <p role="status" className="text-sm font-medium text-status-logged-fg empty:hidden">
      {status}
    </p>
  );
}

export function NotSaved({ id, reason }: { id: string; reason: string | null | undefined }) {
  return reason ? (
    <p
      id={id}
      role="alert"
      className="rounded-row bg-status-needs-client-bg px-3 py-1 text-sm text-status-needs-client-fg"
    >
      Not saved: {reason}
    </p>
  ) : null;
}

/** A note to read before changing what's above it. */
export function Note({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-card border border-line bg-panel-2 px-4 py-3 text-sm text-muted">{children}</div>
  );
}

/** A warning: what changing this does beyond the field. */
export function Warning({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-card bg-status-needs-client-bg px-4 py-3 text-sm text-status-needs-client-fg">
      {children}
    </div>
  );
}

export function NumberField({
  label,
  hideLabel = false,
  unit,
  hint,
  min,
  max,
  step = 1,
  inputMode = "numeric",
  shown,
  status,
  error,
  problem,
  onSave,
}: {
  label: string;
  /** Named by a column's heading: the label is for a screen reader only. */
  hideLabel?: boolean;
  unit: string;
  hint?: ReactNode;
  min: number;
  max?: number;
  step?: number;
  inputMode?: "numeric" | "decimal";
  /** What's being saved, while it saves; then what's saved. */
  shown: number;
  status: string | undefined;
  /** Why the server refused the last change. */
  error: string | undefined;
  /** Why what's typed can't be saved, or null if it can. */
  problem: (text: string) => string | null;
  onSave: (value: number) => void;
}) {
  const id = useId();
  // What's typed and not yet saved, if anything.
  const [draft, setDraft] = useState<string | null>(null);
  const [typedProblem, setTypedProblem] = useState<string | null>(null);
  const reason = typedProblem ?? error;

  function commit() {
    if (draft === null) {
      return;
    }
    const found = problem(draft);
    if (found !== null) {
      setTypedProblem(found);
      return;
    }
    const value = Number(draft.trim());
    setDraft(null);
    if (value !== shown) {
      onSave(value);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className={hideLabel ? "sr-only" : "font-medium"}>
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="number"
          inputMode={inputMode}
          min={min}
          max={max}
          step={step}
          className={`${inputClass} w-28`}
          value={draft ?? String(shown)}
          aria-invalid={Boolean(reason)}
          aria-describedby={
            `${id}-unit` + (hint === undefined ? "" : ` ${id}-hint`) + (reason ? ` ${id}-problem` : "")
          }
          onChange={(event) => {
            setDraft(event.target.value);
            setTypedProblem(null);
          }}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              commit();
            } else if (event.key === "Escape") {
              setDraft(null);
              setTypedProblem(null);
            }
          }}
        />
        <span id={`${id}-unit`} className="text-muted">
          {unit}
        </span>
      </div>
      {hint === undefined ? null : (
        <p id={`${id}-hint`} className="text-sm text-muted">
          {hint}
        </p>
      )}
      <Status status={status} />
      <NotSaved id={`${id}-problem`} reason={reason} />
    </div>
  );
}

export function CheckboxField({
  label,
  hint,
  checked,
  status,
  error,
  onSave,
}: {
  label: string;
  hint: ReactNode;
  /** What's being saved, while it saves; then what's saved. */
  checked: boolean;
  status: string | undefined;
  /** Why the server refused the last change. */
  error: string | undefined;
  onSave: (checked: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label className="flex cursor-pointer items-center gap-2 font-medium">
        <input
          type="checkbox"
          className="size-4 accent-brand"
          checked={checked}
          aria-describedby={`${id}-hint` + (error ? ` ${id}-problem` : "")}
          aria-invalid={Boolean(error)}
          onChange={(event) => {
            onSave(event.target.checked);
          }}
        />
        {label}
      </label>
      <p id={`${id}-hint`} className="text-sm text-muted">
        {hint}
      </p>
      <Status status={status} />
      <NotSaved id={`${id}-problem`} reason={error} />
    </div>
  );
}
